"use client"

import { useSyncExternalStore } from "react"

export type UploadStatus = "uploading" | "success" | "error"

export interface UploadItem {
  /** id único do upload */
  id: string
  /** rótulo amigável exibido no card (ex.: nome do arquivo, "Áudio") */
  label: string
  /** 0-100 */
  progress: number
  status: UploadStatus
  /** mensagem de erro quando status === "error" */
  error?: string
  /** true enquanto o browser ainda não reportou o tamanho total (barra animada) */
  indeterminate?: boolean
  /** aborta o upload (chamado pelo botão de cancelar) */
  cancel: () => void
}

// Store observável a nível de módulo. Os services (que NÃO são componentes
// React) registram uploads aqui; a UI global assina via useSyncExternalStore.
let items: UploadItem[] = []
const listeners = new Set<() => void>()

function notify() {
  listeners.forEach((l) => l())
}

export const uploadStore = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
  getSnapshot(): UploadItem[] {
    return items
  },
  add(item: UploadItem) {
    items = [...items, item]
    notify()
  },
  update(id: string, patch: Partial<UploadItem>) {
    let mudou = false
    items = items.map((it) => {
      if (it.id !== id) return it
      mudou = true
      return { ...it, ...patch }
    })
    if (mudou) notify()
  },
  remove(id: string) {
    const antes = items.length
    items = items.filter((it) => it.id !== id)
    if (items.length !== antes) notify()
  },
}

// Snapshot estável p/ SSR (useSyncExternalStore exige referência constante).
const EMPTY: UploadItem[] = []

/** Hook que devolve a lista reativa de uploads ativos. */
export function useUploads(): UploadItem[] {
  return useSyncExternalStore(
    uploadStore.subscribe,
    uploadStore.getSnapshot,
    () => EMPTY,
  )
}
